// =============================================================================
// Order chain — the quote → order → shipment → invoice → payment trail Epicor
// keeps for a sales order. One reusable panel for the Order and Shipment pages.
//
// Backend: GET /api/sales-orders/{orderNum}/related  (SalesOrderController)
// which wraps Erp.BO.SalesOrderSvc/GetSalesOrderRelationshipMap — the same
// graph Kinetic's own Order Relationship Map draws.
//
// ── Why this is NOT what the orders grid uses ────────────────────────────────
// The grid stamps its pack chip from the in-memory shipment snapshot: a HashSet
// lookup, no Epicor hop, so a 50-row page stays a single request. This panel is
// a live ~200-350ms RPC for ONE order, so it only ever runs when a user has
// opened one order and asked for the chain. It earns that cost by being the
// only place we can see PAYMENTS, the originating QUOTE, and records that have
// aged out of the 12-month cache window.
//
// ── Cost on page open: zero ──────────────────────────────────────────────────
// The fetch lives in this component's mount effect, and the component is only
// mounted once its tab/disclosure is opened. Results are held in a module-level
// cache so reopening is instant; the fetch still re-fires to refresh quietly.
// =============================================================================
import React, { useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import {
  FaFileAlt, FaClipboardList, FaTruck, FaFileInvoiceDollar, FaMoneyCheckAlt,
  FaChevronRight,
} from "react-icons/fa";
import { apiFetch, hasAction } from "../utils/auth";
import { sessionMap } from "../utils/sessionCaches";

const API_URL = process.env.REACT_APP_API_URL;

// orderNum -> response. Survives unmount so reopening the tab paints instantly.
const chainCache = sessionMap(); // emptied on sign-out / new sign-in

// Where each node type opens. Payments have no detail page in this app, so they
// render as plain facts rather than dead links — a link that goes nowhere is
// worse than no link.
const ROUTE = {
  // A role without the target page's action (00032 quote view, 00068 AR)
  // sees the fact without a link instead of a link to "No access".
  quote:    (ref) => (hasAction("00032", true) ? `/quote/edit/${encodeURIComponent(ref)}` : null),
  order:    (ref) => `/order/${encodeURIComponent(ref)}`,
  shipment: (ref) => `/shipment/${encodeURIComponent(ref)}`,
  invoice:  (ref) => (hasAction("00068", true) ? `/invoices/${encodeURIComponent(ref)}` : null),
};

const ICON = {
  quote:    FaFileAlt,
  order:    FaClipboardList,
  shipment: FaTruck,
  invoice:  FaFileInvoiceDollar,
  payment:  FaMoneyCheckAlt,
};

// Chain order is imposed HERE. MapGraph appends groups in Epicor's `pages`
// wire order and sorts nothing — the pinned captures happen to arrive in chain
// order, which is not the same as a guarantee. Anything that is not one of
// these five (kind "other", or a node type Epicor adds later) keeps its wire
// position at the END under its own label rather than being dropped.
const KIND_ORDER = ["quote", "order", "shipment", "invoice", "payment"];

// Box accent per kind. The same hexes the chips carry in the Quote and Order
// grids, but as this panel's OWN classes: .qflow-chip-order / .qflow-chip-ship
// are shared with those grids and carry a cursor:pointer and hover shadow meant
// for a clickable chip — wrong on a box whose ROWS are the links.
const TONE = {
  quote:    "qchain-quote",
  order:    "qchain-order",
  shipment: "qchain-ship",
  invoice:  "qchain-invoice",
  payment:  "qchain-payment",
};

// Chain order + "a type with no records is not rendered at all", in one place.
// One length check covers two cases: the group never arrives (the server drops
// empty node types, so order 155340 comes back quote/order/shipment with no
// invoice group at all), and the group arrives with records: [] because every
// node had a blank link.
function chainBoxes(groups) {
  const all = groups || [];
  const known = KIND_ORDER.flatMap((k) => all.filter((g) => g.kind === k));
  const rest  = all.filter((g) => !KIND_ORDER.includes(g.kind));
  return [...known, ...rest].filter((g) => (g.records || []).length > 0);
}

// One record. The type is named by the box, so no icon repeats per row.
function ChainRecord({ kind, label, record, isHere, onOpen }) {
  const details = (record.details || []).filter(Boolean);

  // Epicor does not guarantee a record NUMBER here. Payments come back as
  // status text — every payment on order 153825 reads "Zero Bal." — and
  // "#Zero Bal." invents a record id. Only number-shaped references get the #.
  const ref = /^[0-9]+$/.test(String(record.reference).trim())
    ? "#" + record.reference
    : record.reference;

  // No link on the record you are standing on, and none for payments: this app
  // has no payment detail page, and a link that goes nowhere is worse than none.
  const href = isHere ? null : ROUTE[kind]?.(record.reference);

  const body = (
    <span className="qchain-rec-m">
      <span className="qchain-rec-id">{ref}</span>
      {details.length > 0 && (
        // NOT details.join(" · ") — each detail is its own element so a wrap
        // falls BETWEEN facts instead of inside "$3,750.00 USD", and the
        // separator is drawn in CSS so the last item never trails a dot.
        <span className="qchain-rec-meta">
          {details.map((d, i) => <span className="qchain-d" key={i}>{d}</span>)}
        </span>
      )}
      {isHere && <span className="qchain-here">you are here</span>}
    </span>
  );

  // A <div>, not an <a> with no href: div.qchain-rec is what turns off the
  // hover tint and the pointer cursor, so a payment row cannot look clickable.
  if (!href) return <div className="qchain-rec">{body}</div>;

  return (
    <a
      href={href}
      className="qchain-rec"
      title={`Open ${label} ${ref}`}
      onClick={(e) => { e.preventDefault(); onOpen(href); }}
    >
      {body}
      <FaChevronRight className="qchain-rec-go" aria-hidden="true" />
    </a>
  );
}

// One record TYPE. Every record of that type is a row inside it — never capped,
// never summarised, and never arrow-joined: three packs of one order are
// siblings, not a sequence. The old layout put an arrow between every pair
// INCLUDING siblings, so two packs read "shipment 228228 -> shipment 228229",
// asserting a causal order that does not exist. Sequence is now asserted only
// BETWEEN boxes, by the connector drawn in .qchain-grp + .qchain-grp::before.
function ChainBox({ group, hereKind, hereRef, onOpen }) {
  const Icon = ICON[group.kind] || FaFileAlt;
  const n = group.records.length;

  return (
    <section className={`qchain-grp ${TONE[group.kind] || ""}`} aria-label={group.label}>
      <div className="qchain-grp-h">
        <Icon className="qchain-ic" aria-hidden="true" />
        {/* group.label is Epicor's own display text ("AR Invoice"). Show it,
            never branch on it — kind is the stable key. */}
        <span className="qw-eyebrow qchain-lab">{group.label}</span>
        {/* Only when it earns its place: a "1" beside a single row is noise, a
            "6" beside six shipments is the point. */}
        {n > 1 && <span className="qchain-n">{n}</span>}
      </div>

      {group.records.map((rec, ri) => (
        // Index first: references repeat inside a group (five payments all
        // reading "Zero Bal.") and two "other" groups can share one.
        <ChainRecord
          key={`${ri}-${rec.reference}`}
          kind={group.kind}
          label={group.label}
          record={rec}
          // Epicor's isCurrent is a GROUP flag pointing at the order the map was
          // drawn for, so it cannot single out one of six packs — trust it only
          // when the group holds exactly one record. Pages that know better
          // (ShipmentView passes the pack it is on) say so explicitly.
          isHere={hereKind
            ? (group.kind === hereKind && String(rec.reference) === String(hereRef))
            : (group.isCurrent && n === 1)}
          onOpen={onOpen}
        />
      ))}
    </section>
  );
}

export default function OrderChainPanel({
  orderNum,
  navigate,
  bare = false,
  // Which node the VIEWER is standing on. Epicor's own isCurrentPage always
  // points at the order the map was drawn for, which is wrong on a shipment
  // page — the reader is on the pack, not the order. Pages that know better
  // say so; everything else falls back to Epicor's flag.
  hereKind,
  hereRef,
}) {
  // Most orders answer in 200-350ms, but a large legacy order can take much
  // longer — order 30602 took 14 seconds. A bare spinner for that long reads as
  // a hang, so say what is happening rather than leaving the reader guessing.
  const [slow, setSlow] = useState(false);

  const [state, setState] = useState(() => ({
    loading: !chainCache.has(orderNum),
    error: null,
    data: chainCache.get(orderNum) || null,
  }));

  useEffect(() => {
    if (!orderNum) return;
    let alive = true;

    // Repaint from cache immediately when the order changes, then refresh.
    setState({
      loading: !chainCache.has(orderNum),
      error: null,
      data: chainCache.get(orderNum) || null,
    });

    setSlow(false);
    const slowTimer = setTimeout(() => { if (alive) setSlow(true); }, 4000);

    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/sales-orders/${encodeURIComponent(orderNum)}/related`);
        const body = await r.json().catch(() => null);

        if (!alive) return;

        if (!r.ok) {
          // 503 = the order cache is still warming; 502 = Epicor wouldn't answer.
          // Neither means "this order has no documents", so don't render empty.
          const msg = r.status === 503
            ? "Order data is still loading. Try again in a moment."
            : body?.error || "Couldn't read the related records from Epicor.";
          setState((s) => (s.data ? { ...s, loading: false } : { loading: false, error: msg, data: null }));
          return;
        }

        chainCache.set(orderNum, body);
        setState({ loading: false, error: null, data: body });
      } catch (e) {
        if (!alive) return;
        setState((s) => (s.data
          ? { ...s, loading: false }
          : { loading: false, error: "Couldn't reach the server.", data: null }));
      }
    })();

    return () => { alive = false; clearTimeout(slowTimer); };
  }, [orderNum]);

  const { loading, error, data } = state;

  const open = (href) => {
    if (navigate) navigate(href);
    else window.location.assign(href);
  };

  // Chain order and the "no records, no box" rule, applied once.
  const boxes = chainBoxes(data?.groups);

  const inner = (
    <>
      {loading && !data && (
        <div className="qchain-msg">
          <Spinner animation="border" size="sm" />{" "}
          {slow
            ? "Still reading the chain from Epicor — a long order history can take a while."
            : "Loading related records…"}
        </div>
      )}

      {!loading && error && !data && (
        <Alert variant="warning" className="qchain-alert">{error}</Alert>
      )}

      {/* The ONE empty state, and it only fires when every type is absent.
          Gated on !loading && !error on purpose: a 503 (order cache warming) or
          a 502 (Epicor wouldn't answer) is not "no documents exist", and saying
          so to a clerk would be false. */}
      {!loading && !error && boxes.length === 0 && (
        <div className="qchain-msg">Nothing else is linked to this order yet.</div>
      )}

      {boxes.length > 0 && (
        <div className="qchain-stack">
          {boxes.map((g, gi) => (
            <ChainBox
              key={`${g.kind}-${gi}`}
              group={g}
              hereKind={hereKind}
              hereRef={hereRef}
              onOpen={open}
            />
          ))}
        </div>
      )}

      {data?.truncated && (
        <div className="qchain-msg qchain-warn">
          Epicor capped this map — there may be more records than are shown.
        </div>
      )}
    </>
  );

  if (bare) return inner;

  return (
    <div className="qchain-wrap">
      <div className="qchain-head">Related records</div>
      {inner}
    </div>
  );
}
